"use client";

import {
  createClient as createSupabaseClient,
  type EmailOtpType,
  type SupabaseClient,
} from "@supabase/supabase-js";
import { env } from "~/env";
import { clearHostAddress } from "~/lib/onboarding/host-address";
import { createClient } from "~/lib/supabase/client";
import { useBookingStore } from "~/store/bookingStore";
import { useExtensionStore } from "~/store/extensionStore";
import { useReplacementStore } from "~/store/replacementStore";
import { toAuthUser } from "./mapping";
import type { AuthChannel, AuthUser } from "./types";

/**
 * Browser auth seam — the single entry point for every auth operation a client
 * component performs. No component touches `supabase.auth.*` directly; they go
 * through `authClient` so the provider is swappable behind this file.
 *
 * ── Ephemeral signup, hidden behind this seam ──────────────────────────────
 * Two underlying clients live here and are never exposed:
 *
 *  • EPHEMERAL client (`@supabase/supabase-js`): holds the session in a
 *    heap-local Map via a custom storage adapter — no cookies, no localStorage,
 *    no sessionStorage for the session itself. It is cleared on any full page
 *    load (refresh / close+reopen / navigate away), so an incomplete signup
 *    never leaves a usable session behind. New sign-ins (OTP + Google OAuth)
 *    land here. (Only the single-use PKCE code-verifier is mirrored to
 *    sessionStorage so it survives the OAuth redirect — see createEphemeralStorage.)
 *  • PERSISTENT client (`@supabase/ssr`, cookie-backed): the normal app session
 *    once the profile is complete.
 *
 * `promoteSignupSession()` copies the in-memory session into the persistent
 * (cookie) client at the completion points in the auth flow. Reads
 * (`getUser` / `getAccessToken`) prefer the ephemeral session until promotion,
 * then the cookie session — so tRPC calls during signup authenticate via the
 * in-memory token (the Bearer fallback in the server context), and the app runs
 * on cookies afterward.
 *
 * Methods that the caller treats as failable THROW the underlying error (its
 * `.message` is preserved for display). `refresh()` is best-effort and never
 * throws, matching every current call site that ignores its result.
 */
export interface AuthClient {
  /** Send a sign-in OTP to the given channel. */
  sendOtp(channel: AuthChannel, identifier: string): Promise<void>;
  /** Verify a primary sign-in OTP (sms / email). */
  verifyPrimaryOtp(
    channel: AuthChannel,
    identifier: string,
    code: string,
  ): Promise<void>;
  /** Verify a contact-change OTP (email_change / phone_change). */
  verifyContactChange(
    channel: AuthChannel,
    identifier: string,
    code: string,
  ): Promise<void>;
  /** Begin Google OAuth (full-page redirect to `redirectTo` on success). */
  signInWithGoogle(redirectTo: string): Promise<void>;
  /** Begin Apple OAuth (full-page redirect to `redirectTo` on success). */
  signInWithApple(redirectTo: string): Promise<void>;
  /**
   * Complete an OAuth / magic-link redirect on the callback page: exchanges the
   * `code` (or verifies the `token_hash`) in the URL into an in-memory session.
   */
  completeOAuthRedirect(): Promise<void>;
  /** The validated current user, or null. */
  getUser(): Promise<AuthUser | null>;
  hasPendingSignup(): boolean;
  /** The current access token for the Bearer header, or null. */
  getAccessToken(): Promise<string | null>;
  /** Best-effort session refresh (e.g. after a profile/contact change). */
  refresh(): Promise<void>;
  /** Sign out the current session (ephemeral and/or persistent). */
  signOut(): Promise<void>;
  /**
   * Promote a completed in-memory signup session into the persistent cookie
   * session. No-op when there is no ephemeral session (e.g. already cookie-based).
   */
  promoteSignupSession(): Promise<void>;
  /**
   * Subscribe to persistent (cookie) auth state. Fires once with the initial
   * state, then on every change. Returns an unsubscribe function.
   */
  onAuthChange(cb: (isAuthenticated: boolean) => void): () => void;
}

// ── Underlying clients ────────────────────────────────────────────────────────

// Persistent cookie client — lazily created (memoized) on first use.
let persistent: ReturnType<typeof createClient> | null = null;
function persistentClient(): ReturnType<typeof createClient> {
  persistent ??= createClient();
  return persistent;
}

/**
 * Storage adapter for the ephemeral client: the session stays in a heap-local
 * Map (gone on reload), but the PKCE `code-verifier` is mirrored to
 * `sessionStorage` so it survives the single OAuth redirect to Google and back.
 * The verifier is short-lived and single-use; the session is never written to
 * any durable store.
 */
function createEphemeralStorage() {
  const mem = new Map<string, string>();
  const isVerifier = (key: string) => key.endsWith("-code-verifier");
  return {
    getItem(key: string): string | null {
      if (isVerifier(key)) {
        try {
          return window.sessionStorage.getItem(key);
        } catch {
          return null;
        }
      }
      return mem.get(key) ?? null;
    },
    setItem(key: string, value: string): void {
      if (isVerifier(key)) {
        try {
          window.sessionStorage.setItem(key, value);
        } catch {
          // sessionStorage unavailable — OAuth will fail loudly on exchange.
        }
        return;
      }
      mem.set(key, value);
    },
    removeItem(key: string): void {
      if (isVerifier(key)) {
        try {
          window.sessionStorage.removeItem(key);
        } catch {
          // nothing to clear
        }
        return;
      }
      mem.delete(key);
    },
  };
}

// Ephemeral in-memory client. Recreated fresh on every page load (module init).
let ephemeral: SupabaseClient | null = null;
// Once a signup session has been promoted to cookies, stop reading the
// (now redundant) ephemeral session — the cookie client owns refresh from here.
let promoted = false;
// Memoized OAuth/magic-link exchange. The `code` and PKCE verifier are
// single-use, so the exchange must run exactly once per page load even if the
// callback effect fires more than once (React Strict Mode double-invoke,
// remount, fast refresh). A second exchange would 403 ("code already used").
let oauthExchange: Promise<void> | null = null;

function ephemeralClient(): SupabaseClient {
  ephemeral ??= createSupabaseClient(
    env.NEXT_PUBLIC_SUPABASE_URL,
    env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_DEFAULT_KEY,
    {
      auth: {
        // persistSession MUST be true — with `false`, supabase-js ignores the
        // `storage` option below and forces its own in-memory store, so the PKCE
        // code-verifier would never reach sessionStorage and would die on the
        // OAuth redirect (pkce_code_verifier_not_found). The ephemerality is
        // enforced by the storage adapter instead: the session lives in a
        // heap-local Map (gone on reload), and ONLY the verifier is mirrored to
        // sessionStorage so it survives the single redirect to Google.
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: false,
        flowType: "pkce",
        storage: createEphemeralStorage(),
        // Distinct key so the verifier/lock namespace can't collide with the
        // persistent cookie client.
        storageKey: "sb-ezspot-signup",
      },
    },
  );
  return ephemeral;
}

/**
 * The client that currently holds a session: the ephemeral one during signup,
 * the persistent one afterward, or null when signed out.
 */
async function activeSessionClient(): Promise<SupabaseClient | null> {
  if (!promoted && ephemeral) {
    const {
      data: { session },
    } = await ephemeral.auth.getSession();
    if (session) return ephemeral;
  }
  const {
    data: { session },
  } = await persistentClient().auth.getSession();
  return session ? persistentClient() : null;
}

// ── Seam ────────────────────────────────────────────────────────────────────

export const authClient: AuthClient = {
  async sendOtp(channel, identifier) {
    // Reauth on an existing session (account settings) stays on that session's
    // client; a fresh sign-in (signup) creates an ephemeral session.
    const client = (await activeSessionClient()) ?? ephemeralClient();
    const { error } =
      channel === "phone"
        ? await client.auth.signInWithOtp({ phone: identifier })
        : await client.auth.signInWithOtp({ email: identifier });
    if (error) throw error;
  },

  async verifyPrimaryOtp(channel, identifier, code) {
    const client = (await activeSessionClient()) ?? ephemeralClient();
    const { error } = await client.auth.verifyOtp(
      channel === "phone"
        ? { phone: identifier, token: code, type: "sms" }
        : { email: identifier, token: code, type: "email" },
    );
    if (error) throw error;
  },

  async verifyContactChange(channel, identifier, code) {
    // Always operates on the existing session (signup ephemeral or app cookie).
    const client = (await activeSessionClient()) ?? ephemeralClient();
    const { error } = await client.auth.verifyOtp(
      channel === "phone"
        ? { phone: identifier, token: code, type: "phone_change" }
        : { email: identifier, token: code, type: "email_change" },
    );
    if (error) throw error;
  },

  async signInWithGoogle(redirectTo) {
    // PKCE on the ephemeral client; the verifier bridges the redirect via
    // sessionStorage (see createEphemeralStorage).
    const { error } = await ephemeralClient().auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo },
    });
    if (error) throw error;
  },

  async signInWithApple(redirectTo) {
    // Same PKCE-on-ephemeral path as Google; the callback exchange is
    // provider-agnostic (see completeOAuthRedirect).
    const { error } = await ephemeralClient().auth.signInWithOAuth({
      provider: "apple",
      options: { redirectTo, scopes: "name email" },
    });
    if (error) throw error;
  },

  async completeOAuthRedirect() {
    // Run-once at the source: repeated calls share the same in-flight/completed
    // promise, so a double-invoked callback effect never triggers a second
    // (failing) exchange on the same single-use code. Only SUCCESS is
    // remembered — on failure the memo is cleared so a later attempt (retry,
    // back-nav) re-runs instead of replaying the cached rejection. The verifier
    // is also reset on signOut and naturally on the next full page load.
    oauthExchange ??= (async () => {
      const url = new URL(window.location.href);
      const code = url.searchParams.get("code");
      const tokenHash = url.searchParams.get("token_hash");
      const type = url.searchParams.get("type");

      if (code) {
        const { error } =
          await ephemeralClient().auth.exchangeCodeForSession(code);
        if (error) throw error;
        return;
      }
      if (tokenHash && type) {
        const { error } = await ephemeralClient().auth.verifyOtp({
          token_hash: tokenHash,
          type: type as EmailOtpType,
        });
        if (error) throw error;
        return;
      }
      throw new Error("No OAuth code or token in callback URL.");
    })().catch((err) => {
      oauthExchange = null;
      throw err;
    });
    return oauthExchange;
  },

  async getUser() {
    const client = (await activeSessionClient()) ?? persistentClient();
    const {
      data: { user },
    } = await client.auth.getUser();
    return user ? toAuthUser(user) : null;
  },

  hasPendingSignup() {
    return !!ephemeral && !promoted;
  },

  async getAccessToken() {
    const client = await activeSessionClient();
    if (!client) return null;
    const {
      data: { session },
    } = await client.auth.getSession();
    return session?.access_token ?? null;
  },

  async refresh() {
    // Best-effort: callers ignore the result today. No active session → nothing
    // to refresh (don't spin up an empty client just to no-op).
    const client = await activeSessionClient();
    if (!client) return;
    await client.auth.refreshSession();
  },

  async signOut() {
    if (ephemeral) {
      try {
        await ephemeral.auth.signOut();
      } catch {
        // ignore — clearing local state regardless
      }
    }
    // On the happy path Supabase clears its own auth cookies here. It only
    // fails to when a corrupt/tampered cookie makes it throw while reading the
    // session to revoke it (before it removes the cookie) — in which case we
    // fall back to clearing them by name below.
    let persistentSignOutFailed = false;
    try {
      await persistentClient().auth.signOut();
    } catch {
      persistentSignOutFailed = true;
    }
    ephemeral = null;
    promoted = false;
    oauthExchange = null;

    // Clear per-tab hold context (booking / extension / replacement) so a
    // revealed spot address or held-spot context can't survive an account
    // switch in the same tab. sessionStorage is keyed per-tab, not per session
    // token, and nothing else clears these stores on sign-out — this resets
    // both the in-memory state and the persisted sessionStorage entry.
    useBookingStore.getState().clear();
    useExtensionStore.getState().clear();
    useReplacementStore.getState().clear();
    // Same for an address picked on the host landing page but not yet saved
    // to a listing.
    clearHostAddress();

    if (typeof window !== "undefined") {
      // Always clear the nav-context hint (our own cookie) so a host's "host"
      // context can't seed the next user who signs in on this browser.
      const namesToClear = ["ezspot_nav_context"];

      // Only reach into Supabase's own auth cookies when its signOut couldn't
      // clear them itself (corrupt session). Normal sign-outs leave cookie
      // lifecycle entirely to the library.
      if (persistentSignOutFailed) {
        for (const cookie of document.cookie.split(";")) {
          const name = cookie.trim().split("=")[0];
          if (name?.includes("-auth-token")) namesToClear.push(name);
        }
      }

      if ("cookieStore" in window) {
        for (const name of namesToClear) {
          void (window.cookieStore as CookieStore).delete({ name, path: "/" });
        }
      } else {
        for (const name of namesToClear) {
          // biome-ignore lint/suspicious/noDocumentCookie: fallback for browsers without the Cookie Store API
          document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax; Secure`;
        }
      }
    }
  },

  async promoteSignupSession() {
    if (promoted || !ephemeral) return;
    const {
      data: { session },
    } = await ephemeral.auth.getSession();
    if (!session) return;

    // Write the in-memory tokens into the cookie client → persistent session.
    // setSession reports failure via the returned `error` (it does NOT throw),
    // so check it: a silent failure would otherwise flip `promoted` and route
    // reads to a cookie client that has no session — a completed signup that
    // appears logged out. Throw instead so finishAuth can recover.
    const { error } = await persistentClient().auth.setSession({
      access_token: session.access_token,
      refresh_token: session.refresh_token,
    });
    if (error) throw error;

    // Only now is the handoff real: stop the ephemeral client refreshing the
    // (now shared) refresh token so the two clients can't race on rotation, and
    // route future reads to the persistent cookie client.
    await ephemeral.auth.stopAutoRefresh();
    promoted = true;
  },

  onAuthChange(cb) {
    const client = persistentClient();
    void client.auth
      .getSession()
      .then(({ data: { session } }) => cb(!!session));
    const {
      data: { subscription },
    } = client.auth.onAuthStateChange((_, session) => cb(!!session));
    return () => subscription.unsubscribe();
  },
};
